import {
  Box2,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Matrix4,
  Object3D,
  Points,
  PointsMaterial,
  Vector2,
  Vector3,
  Vector3Tuple,
} from 'three';

import { FatLine, LineGeometry, LineMaterial } from '@/lib/three';
import { ILineOptions, ILineUserData, WidgetObject } from '@/types';

import { BaseCamera } from '../camera';
import { isLinesIntersectLine, reverseArrayChunks } from '../math';
import { Point3d } from './Point3d';

const _vec = new Vector3();

export interface LineUpdateOptions {
  startIndex?: number;
  mode?: 'update' | 'add' | 'remove' | 'replace';
}

export interface LineUpdateVertexs {
  start: number;
  end: number;
  updateData: number[] | Float32Array;
  pointCount: number;
}

export class Line3d {
  static MIN_POINTS_COUNT = 2;
  static POINT_SIZE = 6;
  static GROW_POINTS = 16;

  /**
   * 创建 3D 折线
   * @param data [x1, y1, z1, x2, y2, z2...] three.js 坐标系
   * @param options 配置项
   * @returns
   */
  static create(data: WidgetObject['data'], options: ILineOptions): FatLine {
    const { color, dashConfig, maxPoints, growable, vertexColors, canvasSize = [1000, 1000] } = options;

    const pointsLen = Math.floor(data.length / 3);
    const growLen = maxPoints && maxPoints > pointsLen ? maxPoints - pointsLen : growable ? Line3d.GROW_POINTS : 0;
    const positions = data.concat(new Array(growLen * 3).fill(0));

    const geometry = new LineGeometry();

    geometry.pointCount = pointsLen;
    geometry.instanceCount = Math.max(pointsLen - 1, 0);

    geometry.setPositions(positions);

    // 如果设置了 vertexColors，就使用顶点颜色
    if (vertexColors) {
      geometry.setColors(vertexColors);
    }

    const matConfig = {
      color,
      linewidth: 2,
      resolution: new Vector2(...canvasSize),
      alphaToCoverage: true,
      dashed: false,
      dashSize: 1,
      gapSize: 1,
      vertexColors: vertexColors ? true : false,
      depthTest: false,
    };

    if (dashConfig) {
      matConfig.dashed = true;
      matConfig.dashSize = dashConfig[0];
      matConfig.gapSize = dashConfig[1];
    }

    const line = new FatLine(geometry, new LineMaterial(matConfig));

    line.name = line.uuid;
    line.userData = { type: 'line3d', ...options } satisfies ILineUserData;

    if (dashConfig) {
      line.computeLineDistances();
    }

    if ((line.userData as ILineUserData).withDot) {
      const dots = Line3d.createPoints(positions, color, line.layers.mask);
      dots.geometry.setDrawRange(0, data.length / 3);
      line.add(dots);
    }

    return line;
  }

  /**
   * 全量更新折线
   * @param target
   * @param data
   */
  static update(target: FatLine, data: WidgetObject['data']) {
    Line3d.partialUpdate(target, data, { startIndex: 0, mode: 'replace' });
  }

  /**
   * 局部更新 3D 折线
   * @param target 目标物
   * @param data 待更新数据 [x1, y1, z1, x2, y2, z2...] three.js 坐标系
   * @param options
   */
  static partialUpdate(target: FatLine, data: WidgetObject['data'], options?: LineUpdateOptions) {
    const positions = Line3d.getVertexFromObject3D(target);
    Line3d.partialUpdateFromVertexs(target, positions, Line3d.getUpdateVertexs(positions, data, options));
  }

  /**
   * 根据 widget 生成折线
   * @param name
   * @param widget
   * @param matrix
   * @param options
   */
  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: ILineOptions): FatLine {
    const data = Line3d.pointsApplyMatrix4(widget.data, matrix);

    const line = Line3d.create(data, options);
    line.name = name;

    return line;
  }

  static updateFromWidget(target: FatLine, widget: WidgetObject, matrix: Matrix4) {
    Line3d.update(target, Line3d.pointsApplyMatrix4(widget.data, matrix));
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix 从 threejs 到文件坐标系
   */
  static transOutWidget(target: FatLine, matrix: Matrix4): WidgetObject {
    const data = Line3d.pointsApplyMatrix4(Line3d.getVertexFromObject3D(target), matrix);

    return {
      name: 'line3d',
      data,
      line_type: 'line',
    };
  }

  /**
   * 比较当前折线对象长度，如果不够则拓展
   * @param target
   * @param newLength
   * @returns 如折线被拓展，则返回拓展后的数组；否则返回 null
   */
  static extend(target: FatLine, newLength: number) {
    let newPositions: Float32Array | null = null;

    if (newLength > target.geometry.attributes.instanceEnd.count + 1) {
      const newLen = (newLength + (target.userData.growable ? Line3d.GROW_POINTS : 0)) * 3;
      newPositions = new Float32Array(newLen);
    }

    return newPositions;
  }

  /**
   * 根据顶点数据更新线条
   * @param target
   * @param originData
   * @param toUpdateData
   */
  static partialUpdateFromVertexs(
    target: FatLine,
    originData: Float32Array | Array<number>,
    { start, end, updateData, pointCount }: LineUpdateVertexs
  ) {
    target.geometry.pointCount = pointCount;

    const newPositions = Line3d.extend(target, pointCount);
    if (newPositions) {
      // 复制之前的点
      for (let i = 0; i < start; i++) {
        newPositions[i] = originData[i];
      }
      // 更新新的点，会同步更新渲染
      target.geometry.setPositions(newPositions);
      // 扩大 points 的顶点数组
      const points = Line3d.getPoints(target);
      if (points) {
        Line3d.setPointsGeometry(points.geometry, newPositions, target.userData.color);
      }
    }

    target.geometry.instanceCount = Math.max(pointCount - 1, 0);
    target.geometry.updatePositions(updateData, start);
    // 更新虚线
    target.userData.dashConfig && target.computeLineDistances();
    // 更新点坐标
    target.userData.withDot && Line3d.updatePoints(target, start, end, updateData, pointCount);
  }

  static setPointsGeometry(geometry: BufferGeometry, positions: number[] | Float32Array, color: string) {
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    const colors: number[] = [];
    const colorHex = new Color(color);
    for (let i = 0; i < positions.length; i = i + 3) {
      colors.push(colorHex.r, colorHex.g, colorHex.b);
    }
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    geometry.computeBoundingSphere();
  }

  /**
   * 创建线条上的 2D 点集
   * @param positions 点集位置数组
   * @param color 点的颜色
   * @param mask 传入的图层，确保点与线的图层一致
   * @returns Points 点集
   */
  static createPoints(positions: number[] | Float32Array, color: string, mask?: number) {
    const geometry = new BufferGeometry();
    Line3d.setPointsGeometry(geometry, positions, color);
    const material = new PointsMaterial({
      size: Line3d.POINT_SIZE,
      vertexColors: true,
      sizeAttenuation: false,
      transparent: false,
      depthTest: false,
    });
    const points = new Points(geometry, material);
    if (mask !== undefined) {
      points.layers.mask = mask;
    }

    return points;
  }

  static updatePoints(
    target: FatLine,
    start: number,
    end: number,
    updateData: Float32Array | number[],
    pointCount: number
  ) {
    const points = Line3d.getPoints(target);
    if (points) {
      points.geometry.attributes.position.needsUpdate = true;
      for (let i = start, j = 0; i < end; i = i + 3, j = j + 3) {
        points?.geometry.attributes.position.setXYZ(i / 3, updateData[j], updateData[j + 1], updateData[j + 2]);
      }
      points.geometry.setDrawRange(0, pointCount);
      points.geometry.computeBoundingSphere();
    }
  }

  /**
   * 把整条线移动到指定位置
   * @param data 顶点数据
   * @param position 第一个点需要移动到的位置
   * @returns
   */
  static getMovedGeometryToPosition(data: Float32Array | number[], [x, y, z]: [number, number, number]): number[] {
    // 减去第一个点原来的位置，得到需要偏移的量
    data = [...data];
    const displacementX = x - data[0];
    const displacementY = y - data[1];
    const displacementZ = z - data[2];
    for (let i = 0; i < data.length; i += 3) {
      data[i] += displacementX;
      data[i + 1] += displacementY;
      data[i + 2] += displacementZ;
    }
    return data;
  }

  /**
   * 更新点集的顶点坐标数据
   * @param positions 点集的原始位置，[x1,y1,z1,x2,y2,z2,...]
   * @param data 需要更新的顶点位置，[x1,y1,z1,x2,y2,z2,...]
   * @param options 配置：startIndex 指从哪个顶点开始更新 data，mode 指更新模式（默认是 update ）。
   * update - 从 data 更新顶点数据
   * add - 在 startIndex 之后添加 data
   * remove - 移除startIndex 位置的顶点;
   * replace - 用 data 替换掉 startIndex 之后的所有数据
   * @returns 返回一个对象，包括: start 表示开始的索引，end 表示结束索引，updateData 为需要更新的顶点位置数组，pointCount 表示更新后顶点的总数。
   */
  static getUpdateVertexs(
    positions: Float32Array | Vector3Tuple | number[],
    data: number[],
    options?: LineUpdateOptions
  ) {
    const { startIndex = 0, mode = 'update' } = options || {};
    const start = startIndex * 3;
    let updateData: Array<number> | Float32Array = [];
    let end = positions.length;

    switch (mode) {
      case 'update':
      case 'replace':
        updateData = data;
        end = start + data.length;
        break;

      case 'add':
        updateData = [...data, ...positions.slice(start, end)];
        end += data.length;
        break;

      case 'remove':
        // 仅删除一个点
        updateData = positions.slice(start + 3, end);
        end -= 3;
        break;

      default:
        break;
    }

    const pointCount = Math.floor(
      (mode === 'remove' || mode === 'replace' ? end : Math.max(end, positions.length)) / 3
    );

    return {
      start,
      end,
      updateData,
      pointCount,
    };
  }

  static changeColor(target: FatLine, colorHex: string, refreshData = true) {
    target.traverse((object) => {
      if (object instanceof FatLine && 'color' in object.material) {
        object.material.color.set(colorHex);
      }

      if (object instanceof Points) {
        const colorAttribute = object.geometry.getAttribute('color');
        const color = new Color(colorHex);

        for (let i = 0; i < object.geometry.drawRange.count; i++) {
          colorAttribute.setXYZ(i, color.r, color.g, color.b);
        }
        colorAttribute.needsUpdate = true;
      }
    });

    if (refreshData) {
      target.userData.color = colorHex;
    }
  }

  /**
   * 给线上的某个点渲染成 colorHex 的颜色，其他点还原成默认的颜色
   * @param target 3d 的线
   * @param pointId 修改的点位置
   * @param colorHex 索引为 pointId 的点预期修改成的颜色
   * @param defaultColorHex 其他点要还原成的颜色
   */
  static changePointColor(target: FatLine, pointId: number, colorHex: string, defaultColorHex?: string) {
    const points = Line3d.getPoints(target);
    if (!points) return;
    const color = new Color(colorHex);
    const defaultColor = new Color(defaultColorHex ?? target.userData.color);

    const colorAttribute = points.geometry.getAttribute('color');
    for (let i = 0; i < points.geometry.drawRange.count; i++) {
      if (i === pointId) {
        colorAttribute.setXYZ(pointId, color.r, color.g, color.b);
      } else {
        colorAttribute.setXYZ(i, defaultColor.r, defaultColor.g, defaultColor.b);
      }
    }
    colorAttribute.needsUpdate = true;
  }

  static changePointsSize(target: FatLine, size: number) {
    target.traverse((object) => {
      if (object instanceof Points) {
        object.material.size = size;
      }
    });
  }

  static pointsApplyMatrix4(data: number[] | ArrayLike<number>, matrix: Matrix4) {
    const vec = new Vector3();
    const len = Math.floor(data.length / 3) * 3;

    const newData = new Array(len);
    for (let i = 0; i < data.length; i += 3) {
      vec.fromArray(data, i).applyMatrix4(matrix).toArray(newData, i);
    }

    return newData;
  }

  /**
   * 得到标注物的点数
   * @param target
   * @returns
   */
  static getVertexCount(target: FatLine) {
    return target.geometry.pointCount;
  }

  /**
   * 获取当前线条的顶点数据
   * @param target 线条对象
   * @returns 顶点数组 [x1, y1, z1, ...]
   */
  static getVertexFromObject3D(target: FatLine): Float32Array | Array<number> {
    if (target.geometry.pointCount === 0) return [];

    const start = target.geometry.attributes.instanceStart;

    const segmentCount = Math.min(target.geometry.instanceCount, start.count);

    if (segmentCount === 0) {
      return _vec.fromBufferAttribute(start, 0).toArray();
    }

    const pointCount = segmentCount + 1;
    const positions = new Float32Array(pointCount * 3);
    for (let i = 0, j = 0; i < segmentCount; i++, j += 3) {
      _vec.fromBufferAttribute(start, i).toArray(positions, j);
    }
    // 处理结束点
    const end = target.geometry.attributes.instanceEnd;
    _vec.fromBufferAttribute(end, segmentCount - 1).toArray(positions, segmentCount * 3);

    return positions;
  }

  /**
   * 标准映射方法
   * @param data 折线点坐标
   * @param matrix 外参矩阵
   * @param cameraModel 相机模型
   * @param imageSize 图片宽高
   * @returns number[] [x1, y1, x2, y2, ...]
   */
  static getFusionMapped(
    data: number[],
    matrix: Matrix4,
    cameraModel: BaseCamera,
    imageSize?: { width: number; height: number }
  ): WidgetObject | undefined {
    if (!imageSize) return undefined;
    const interpolatedData = Line3d.interpolatePoints(data, 3);

    // 图片文件坐标系的点 [x1,y1,x2,y2]
    const imageXY: number[] = [];
    for (let i = 0; i < interpolatedData.length; i = i + 3) {
      const point = Point3d.getVectorFusionMapped(_vec.fromArray(interpolatedData, i), matrix, cameraModel);
      if (point) {
        imageXY.push(point.x, point.y);
      }
    }
    if (imageXY.length < 4) return undefined;

    const rectVertex = [
      new Vector2(0, 0),
      new Vector2(imageSize.width, 0),
      new Vector2(imageSize.width, imageSize.height),
      new Vector2(0, imageSize.height),
    ];
    const box = new Box2().setFromPoints(rectVertex);
    const q1 = new Vector2();
    const q2 = new Vector2();

    let left = 0;
    let right = imageXY.length - 1;

    for (let i = 0; i < imageXY.length - 3; i = i + 2) {
      q1.fromArray(imageXY, i);
      q2.fromArray(imageXY, i + 2);
      if (box.containsPoint(q1) || isLinesIntersectLine(rectVertex, q1, q2)) {
        break;
      } else {
        left = left + 2;
      }
    }
    for (let i = imageXY.length - 1; i > left + 2; i = i - 2) {
      q1.fromArray(imageXY, i - 1);
      q2.fromArray(imageXY, i - 3);
      if (box.containsPoint(q1) || isLinesIntersectLine(rectVertex, q1, q2)) {
        break;
      } else {
        right = right - 2;
      }
    }
    let points = imageXY.slice(left, right + 2);

    // 生成的 2D 折线数据 [x1, y1, x2, y2,...] 需为偶数
    if (points.length % 2 !== 0) {
      points = points.slice(0, points.length - 1);
    }

    // 生成条件：至少有两个点
    return points.length >= 4
      ? {
          name: 'line2d',
          data: points,
        }
      : undefined;
  }

  /**
   * 对给定的折线点坐标进行插值
   * @param {Array} data 折线点坐标数组 [x1, y1, z1, x2, y2, z2, ...]
   * @param {number} step 插值步长
   * @returns {Array} 插值后的新数组
   */
  static interpolatePoints(origin: number[], step = 10) {
    const length = origin.length;
    if (length < 6) return origin;
    const data = length % 3 === 0 ? origin : origin.slice(0, Math.floor(length / 3) * 3);
    const interpolatedData = [];

    for (let i = 0; i < data.length - 3; i += 3) {
      const x1 = data[i];
      const y1 = data[i + 1];
      const z1 = data[i + 2];
      const x2 = data[i + 3];
      const y2 = data[i + 4];
      const z2 = data[i + 5];

      const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2);
      const numberOfSteps = Math.ceil(distance / step);
      const dx = (x2 - x1) / numberOfSteps;
      const dy = (y2 - y1) / numberOfSteps;
      const dz = (z2 - z1) / numberOfSteps;

      interpolatedData.push(x1, y1, z1);
      for (let j = 1; j < numberOfSteps; j++) {
        const newX = x1 + j * dx;
        const newY = y1 + j * dy;
        const newZ = z1 + j * dz;

        interpolatedData.push(newX, newY, newZ);
      }
    }

    // 添加最后一个点的坐标
    const lastIndex = data.length - 3;
    interpolatedData.push(data[lastIndex], data[lastIndex + 1], data[lastIndex + 2]);

    return interpolatedData;
  }

  // 查询 Points 点集对象
  static getPoints(target: FatLine) {
    return target?.children.find((child: Object3D): child is Points => {
      return child instanceof Points;
    });
  }

  /**
   * 拼接折线。
   * @param line0 第一条线 line0
   * @param line1 第二条线 line1
   * @param threshold 如果被拼接的两端点距离小于阈值，两端点合并。
   * @returns 拼接后的折线，方向为第一条线 line0 的方向。
   */
  static connectLines(line0: number[], line1: number[], threshold: number) {
    const length0 = line0.length;
    const P = new Vector3().fromArray(line0);
    const Q = new Vector3().fromArray(line0, length0 - 3);

    const length1 = line1.length;
    const M = new Vector3().fromArray(line1);
    const N = new Vector3().fromArray(line1, length1 - 3);

    let minDistanceSq = P.distanceToSquared(M);
    let closest = ['start', 'start'];

    let distance = P.distanceToSquared(N);
    if (distance < minDistanceSq) {
      minDistanceSq = distance;
      closest[1] = 'end';
    }

    distance = Q.distanceToSquared(M);
    if (distance < minDistanceSq) {
      minDistanceSq = distance;
      closest[0] = 'end';
      closest[1] = 'start';
    }

    distance = Q.distanceToSquared(N);
    if (distance < minDistanceSq) {
      minDistanceSq = distance;
      closest[0] = 'end';
      closest[1] = 'end';
    }

    const mode = closest.join('+');
    const result = [];

    // TODO(xiyan): 待简化
    if (minDistanceSq < threshold * threshold) {
      switch (mode) {
        case 'start+start':
          result.push(...reverseArrayChunks(line1, 3), ...line0.slice(3));
          break;
        case 'end+start':
          result.push(...line0, ...line1.slice(3));
          break;
        case 'start+end':
          result.push(...line1, ...line0.slice(3));
          break;
        case 'end+end':
          result.push(...line0, ...reverseArrayChunks(line1, 3).slice(3));
          break;
      }
    } else {
      switch (mode) {
        case 'start+start':
          result.push(...reverseArrayChunks(line1, 3), ...line0);
          break;
        case 'end+start':
          result.push(...line0, ...line1);
          break;
        case 'start+end':
          result.push(...line1, ...line0);
          break;
        case 'end+end':
          result.push(...line0, ...reverseArrayChunks(line1, 3));
          break;
      }
    }

    return result;
  }

  /**
   * 根据比例 t 获取折线上的点
   * @param line 折线对象（FatLine）
   * @param t 比例 (0 到 1)
   * @param target 用于存储结果的 Vector3 对象
   * @returns 返回目标点的 Vector3
   */
  static at(line: FatLine, t: number, target: Vector3): Vector3 {
    const positions = Line3d.getVertexFromObject3D(line); // 获取折线顶点数据
    const segmentCount = positions.length / 3 - 1; // 线段数量

    // 确保 t 在 [0, 1] 范围内
    t = Math.max(0, Math.min(1, t));

    // 计算目标点所在的线段索引
    const segmentIndex = Math.floor(t * segmentCount);

    // 获取线段的起点和终点
    const startIndex = segmentIndex * 3;
    const endIndex = startIndex + 3;

    const start = new Vector3(positions[startIndex], positions[startIndex + 1], positions[startIndex + 2]);
    const end = new Vector3(positions[endIndex], positions[endIndex + 1], positions[endIndex + 2]);
    const localT = (t * segmentCount) % 1; // 当前线段内的局部比例

    // 插值计算目标点
    return target.copy(start).lerp(end, localT);
  }
}
